import React, { useContext, useEffect, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Navigate } from "react-router-dom";
import { Drawer, createStyles, Modal, Stack, Button } from "@mantine/core";
import { AuthContext } from "../../../providers/AuthProvider";
import useReconEventInstanceListApiService from "../services/api/GetReconListApiService";
import ReconEventInstance from "../services/model/ReconEventInstance";
import { queryClient } from "../../../App";
import ProgressIndicator from "../../../themes/components/ProgressIndicator";
import ErrorPage from "../../../common/pages/ErrorPage";
import ReconInstanceDetails from "../components/ReconInstanceDetails";
import { useEnabledFeatureFlag } from "../../../common/feature-flagging/FeatureFlagProvider";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import UnauthorizedErrorPage from "../../../common/pages/UnauthorizedErrorPage";
import NotFound from "../../../common/pages/NotFound";
import { AccessPermissionContext } from "../../../providers/AccessPermissionsProvider";

const localizer = momentLocalizer(moment);

const useStyles = createStyles(() => ({
  ".rbc-calendar": {
    ".rbc-today": {
      backgroundColor: "#ED7687",
      opacity: 0.2,
    },
    ".rbc-event.rbc-selected": {
      backgroundColor: "#062133",
      opacity: 0.8,
    },
    ".rbc-event": {
      backgroundColor: "#062133",
    },
    ".rbc-event:hover": {
      backgroundColor: "#ED7687",
    },
  },
}));

interface CalendarView {
  startTime: string;
  endTime: string;
}

interface CalendarEvent {
  id: string;
  title: string;
  allDay: boolean;
  start: Date;
  end: Date;
}

const ReconsLandingPage: React.FC = () => {
  // Move all hooks to the top level
  const auth = useContext(AuthContext);
  const { hasPermissions } = useContext(AccessPermissionContext);
  const { classes } = useStyles();
  const [selectedInstance, setSelectedInstance] =
    useState<ReconEventInstance | null>(null);
  const hasFeatureEnabled = useEnabledFeatureFlag(
    FeatureFlags.EMPLOYER_PORTAL_AUTOMATED_ODP_INVOICING_VIEW
  );
  const [calendarView, setCalendarView] = useState<CalendarView>({
    startTime: moment().startOf("month").format("YYYY-MM-DD"),
    endTime: moment()
      .startOf("month")
      .add(1, "month")
      .add(7, "days")
      .format("YYYY-MM-DD"),
  });

  // State for managing the "show more" modal
  const [showMoreEvents, setShowMoreEvents] = useState<CalendarEvent[] | null>(null);
  const [showMoreDate, setShowMoreDate] = useState<Date | null>(null);

  const { isLoading, isError, error, data } =
    useReconEventInstanceListApiService({
      startTime: calendarView.startTime,
      endTime: calendarView.endTime,
      employerId: auth?.employer?.id ?? null,
    });

  // Helper functions
  const reValidateQuery = (): void => {
    setSelectedInstance(null);
    queryClient.invalidateQueries("ReconEventInstanceListApiService");
  };

  const updateDateRange = (dateRanges: any): void => {
    switch (dateRanges.length) {
      case 7:
        setCalendarView({
          startTime: moment(dateRanges[0]).format("YYYY-MM-DD"),
          endTime: moment(dateRanges[6]).format("YYYY-MM-DD"),
        });
        break;
      default:
        setCalendarView({
          startTime: moment(dateRanges.start).format("YYYY-MM-DD"),
          endTime: moment(dateRanges.end).format("YYYY-MM-DD"),
        });
        break;
    }
  };

  const handleShowMore = (events: CalendarEvent[], date: Date): void => {
    setShowMoreEvents(events);
    setShowMoreDate(date);
  };

  const closeShowMoreModal = (): void => {
    setShowMoreEvents(null);
    setShowMoreDate(null);
  };

  const handleEventClickFromModal = (eventId: string): void => {
    if (!data) {
      closeShowMoreModal();
      return;
    }
    const instance = data.find(
      (item: ReconEventInstance) => item.id === eventId
    ) || null;
    setSelectedInstance(instance);
    closeShowMoreModal();
  };

  useEffect(() => {
    reValidateQuery();
  }, [calendarView]);

  // Early returns for authentication and authorization
  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  if (!hasPermissions) {
    return <NotFound />;
  }

  if (isLoading) {
    return <ProgressIndicator />;
  }

  if (isError) {
    return <ErrorPage errors={error} />;
  }

  if (!hasFeatureEnabled) {
    return <UnauthorizedErrorPage />;
  }

  // Main render
  return (
    <>
      <Drawer
        opened={selectedInstance !== null}
        size="xl"
        onClose={() => setSelectedInstance(null)}
        title="Recon Details"
        overlayOpacity={0.1}
        overlayBlur={3}
        position="right"
      >
        <div style={{ padding: "25px", maxHeight: "100vh", overflow: "auto" }}>
          <ReconInstanceDetails
            onSuccess={() => reValidateQuery()}
            reconInstance={selectedInstance}
          />
        </div>
      </Drawer>

      {/* Modal for "show more" events */}
      <Modal
        opened={showMoreEvents !== null}
        onClose={closeShowMoreModal}
        title={
          showMoreDate
            ? `Events on ${moment(showMoreDate).format("MMMM D, YYYY")}`
            : "Events"
        }
        size="md"
        centered
      >
        <Stack spacing="xs">
          {showMoreEvents?.map((event: CalendarEvent) => (
            <Button
              key={event.id}
              variant="light"
              fullWidth
              onClick={() => handleEventClickFromModal(event.id)}
              styles={{
                root: {
                  height: "auto",
                  padding: "12px 16px",
                },
                label: {
                  whiteSpace: "normal",
                  textAlign: "left",
                },
              }}
            >
              {event.title}
            </Button>
          ))}
        </Stack>
      </Modal>

      <Calendar
        className={classes[".rbc-calendar"]}
        views={["month", "week"]}
        localizer={localizer}
        startAccessor="start"
        endAccessor="end"
        style={{
          margin: "10px",
          minHeight: "820px",
          maxHeight: "98vh",
        }}
        events={
          data
            ? data.map((event: ReconEventInstance) => ({
                id: event.id,
                title: event.summary,
                allDay: true,
                start: new Date(event.start.date),
                end: new Date(event.start.date),
              }))
            : []
        }
        onSelectEvent={(event: any) => {
          setSelectedInstance(
            data.find(
              (instance: ReconEventInstance) => instance.id === event.id
            ) || null
          );
        }}
        onRangeChange={(dateRanges: any) => updateDateRange(dateRanges)}
        onShowMore={(events: CalendarEvent[], date: Date) => handleShowMore(events, date)}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onDrillDown={() => {}} // Prevent drill-down behavior explicitly
        doShowMoreDrillDown={false}
      />
    </>
  );
};

export default ReconsLandingPage;
